/* import __COLOCATED_TEMPLATE__ from './outbound-whatsapp.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import type PartGroup from 'embercom/objects/inbox/conversation-stream/part-group';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import type Whatsapp from 'embercom/objects/inbox/renderable/whatsapp';
import type Session from 'embercom/services/session';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { Channel } from 'embercom/models/data/inbox/channels';
import { type SenderSummary } from 'embercom/objects/inbox/renderable/whatsapp';
import { OutboundInitialPartSenderType } from 'embercom/objects/inbox/outbound-initial-part-sender-types';
import type ConversationTranslationSettings from 'embercom/services/conversation-translation-settings';
import type Conversation from 'embercom/objects/inbox/conversation';
import {
  isTranslatingPartGroup,
  translatedPartExists,
  hasTranslationStatusInPartGroup,
  getAllTranslationLanguageCodesFromPartGroup,
  getOriginalLanguageCodeFromPartGroup,
  getLanguageLabelFromCode,
} from '../helpers/translations';
import type IntlService from 'ember-intl/services/intl';
import {
  nextInCycle,
  normalizeLocale,
  getDefaultWorkspaceLocale,
} from '../helpers/language-selection';
import { computeTranslationUi } from '../helpers/translation-ui';

interface OutboundWhatsappRenderablePart extends RenderablePart {
  renderableData: Whatsapp;
}

interface OutboundWhatsappPartGroup extends PartGroup {
  parts: Array<OutboundWhatsappRenderablePart>;
}

interface Args {
  partGroup: OutboundWhatsappPartGroup;
  conversation?: Conversation;
  isReadOnly: boolean;
}

interface Signature {
  Args: Args;
}

export default class ConversationStreamTeammateContentComponent extends Component<Signature> {
  @service declare session: Session;
  @service declare conversationTranslationSettings: ConversationTranslationSettings;
  @service declare intl: IntlService;

  @tracked selectedLangCode?: string;

  whatsappChannel = Channel.Whatsapp;

  get renderableData() {
    return this.args.partGroup.parts[0].renderableData;
  }

  get isDeleted() {
    return this.renderableData.deletedAt;
  }

  get sender(): SenderSummary {
    return this.renderableData.senderSummary;
  }

  get senderIsTeammate() {
    return this.isDeleted || this.renderableData.senderType === OutboundInitialPartSenderType.Admin;
  }

  get senderIsTeam() {
    return this.renderableData.senderType === OutboundInitialPartSenderType.Team;
  }

  get senderIsBot() {
    return this.renderableData.senderType === OutboundInitialPartSenderType.Bot;
  }

  get autoTranslationEnabled() {
    return this.conversationTranslationSettings.autoTranslationEnabledForConversationObject(
      this.args.conversation,
    );
  }

  get availableTranslationCodes(): string[] {
    return getAllTranslationLanguageCodesFromPartGroup(this.args.partGroup);
  }

  get originalLanguageCode(): string | undefined {
    return getOriginalLanguageCodeFromPartGroup(this.args.partGroup);
  }

  get defaultLanguageCode(): string | undefined {
    return getDefaultWorkspaceLocale(this.session);
  }

  private get translationUi() {
    return computeTranslationUi({
      session: this.session,
      available: this.availableTranslationCodes,
      preferred: this.session?.teammate?.teammatePreferredLocale,
      defaultLang: this.defaultLanguageCode,
      original: this.originalLanguageCode,
      selectedLangCode: this.selectedLangCode,
    });
  }

  get cycleOrderCodes(): string[] {
    return this.translationUi.cycleOrderCodes;
  }

  get currentSelectedCode(): string | undefined {
    return this.translationUi.currentSelectedCode;
  }

  get translatedLanguage(): string {
    if (this.conversationLanguageCode) {
      return this.conversationLanguageLabel;
    }
    let original = normalizeLocale(this.originalLanguageCode);
    let code =
      this.cycleOrderCodes.find((c) => c !== original) ||
      this.defaultLanguageCode ||
      this.session.workspace?.locale;
    return code ? getLanguageLabelFromCode(code, this.intl.primaryLocale) || '' : '';
  }

  get conversationLanguageCode(): string | undefined {
    let raw = this.args.conversation?.getAttributeForDescriptor('language')?.value as
      | string
      | undefined;
    return normalizeLocale(typeof raw === 'string' ? raw : undefined);
  }

  get conversationLanguageLabel(): string {
    let code = this.conversationLanguageCode;
    if (!code) {
      return '';
    }
    return getLanguageLabelFromCode(code, this.intl.primaryLocale) || '';
  }

  get showOriginalMessage(): boolean {
    if (!this.translatedPartExists) {
      return true;
    }
    return normalizeLocale(this.currentSelectedCode) === normalizeLocale(this.originalLanguageCode);
  }

  get showTranslatedMessage() {
    return !this.showOriginalMessage;
  }

  get translatedPartExists() {
    return translatedPartExists(this.args.partGroup);
  }

  get isTranslating() {
    return isTranslatingPartGroup(this.args.partGroup);
  }

  get showTranslationToggle() {
    if (hasTranslationStatusInPartGroup(this.args.partGroup)) {
      return true;
    }
    return true;
  }

  get selectedTranslationLanguageCode(): string | undefined {
    return this.currentSelectedCode;
  }

  get showTranslationQualityFeedback() {
    return this.session.workspace.isFeatureEnabled('translation-quality-feedback');
  }

  get selectedLanguageLabel(): string {
    let code = this.currentSelectedCode;
    if (!code) {
      return '';
    }
    return getLanguageLabelFromCode(code, this.intl.primaryLocale) || '';
  }

  get translatedLanguageCode(): string | undefined {
    if (this.conversationLanguageCode) {
      return this.conversationLanguageCode;
    }
    let original = normalizeLocale(this.originalLanguageCode);
    return (
      this.cycleOrderCodes.find((c) => c !== original) ||
      this.defaultLanguageCode ||
      this.session.workspace?.locale
    );
  }

  get currentTranslationAudience(): 'inbound' | 'outbound' {
    let current = normalizeLocale(this.currentSelectedCode);
    let customerCode = normalizeLocale(this.translatedLanguageCode);
    if (customerCode && current === customerCode) {
      return 'outbound';
    }
    return 'inbound';
  }

  @action toggleTranslation() {
    let order = this.cycleOrderCodes;
    let next = nextInCycle(this.currentSelectedCode, order);
    if (next) {
      this.selectedLangCode = next;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationStream::PartGroups::OutboundWhatsapp': typeof ConversationStreamTeammateContentComponent;
    'inbox2/conversation-stream/part-groups/outbound-whatsapp': typeof ConversationStreamTeammateContentComponent;
  }
}
